import React, { useState, useEffect } from 'react';
import {
    CheckSquare, X, Loader2, Users, Clock, AlertTriangle, AlertCircle,
    Shield, Calendar, UserX, FileWarning, Zap, List, Filter, Mail, XCircle,
    AlertOctagon, Send, ChevronLeft
} from 'lucide-react';

export type ToolbarMode = 'idle' | 'check' | 'consent';

export interface QuickSelectOption {
    label: string;
    value: string;
    description?: string;
}

export interface QuickSelectGroup {
    label: string;
    options: QuickSelectOption[];
    isFooter?: boolean;
    mode?: 'check' | 'consent';
}

interface SelectionToolbarProps {
    selectedCount: number;
    totalFilteredCount: number;
    isSelectAllMode: boolean;
    isLoading: boolean;
    isStartingBulkCheck: boolean;
    isStartingBulkConsent: boolean;
    onClearSelection: () => void;
    onQuickSelect: (option: string) => void;
    onStartBulkCheck: () => void;
    onStartBulkConsent: () => void;
    onModeChange: (mode: ToolbarMode) => void;
    resetKey: number;
    quickSelectGroups: QuickSelectGroup[];
}

const CHECK_CHIPS: { value: string; label: string; icon: React.ReactNode }[] = [
    { value: 'high_risk',       label: 'High risk',         icon: <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> },
    { value: 'medium_risk',     label: 'Medium risk',       icon: <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> },
    { value: 'low_risk',        label: 'Low risk',          icon: <Shield className="w-3.5 h-3.5 text-green-600" /> },
    { value: 'due',             label: 'Due for check',     icon: <Calendar className="w-3.5 h-3.5 text-blue-500" /> },
    { value: 'never_checked',   label: 'Never checked',     icon: <UserX className="w-3.5 h-3.5 text-gray-500" /> },
    { value: 'critical_issues', label: 'Critical issues',   icon: <FileWarning className="w-3.5 h-3.5 text-red-600" /> },
    { value: 'high_risk_due',   label: 'High risk + due',   icon: <Zap className="w-3.5 h-3.5 text-red-500" /> },
    { value: 'medium_risk_due', label: 'Medium risk + due', icon: <Zap className="w-3.5 h-3.5 text-amber-500" /> },
];

const CONSENT_CHIPS: { value: string; label: string; icon: React.ReactNode }[] = [
    { value: 'no_consent',        label: 'No consent',      icon: <AlertOctagon className="w-3.5 h-3.5 text-gray-500" /> },
    { value: 'expired_consent',   label: 'Expired consent', icon: <XCircle className="w-3.5 h-3.5 text-red-500" /> },
    { value: 'expiring_consent',  label: 'Expiring soon',   icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> },
];

const SCOPE_CHIPS: { value: string; label: string; icon: React.ReactNode }[] = [
    { value: 'current_page', label: 'Current page', icon: <List className="w-3.5 h-3.5 text-indigo-500" /> },
    { value: 'all_filtered', label: 'All matching',  icon: <Filter className="w-3.5 h-3.5 text-indigo-500" /> },
    { value: 'all_drivers',  label: 'All drivers',  icon: <Users className="w-3.5 h-3.5 text-indigo-500" /> },
];

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
    selectedCount,
    totalFilteredCount,
    isSelectAllMode,
    isLoading,
    isStartingBulkCheck,
    isStartingBulkConsent,
    onClearSelection,
    onQuickSelect,
    onStartBulkCheck,
    onStartBulkConsent,
    onModeChange,
    resetKey,
    quickSelectGroups,
}) => {
    const [mode, setMode] = useState<ToolbarMode>('idle');
    const [activeChip, setActiveChip] = useState<string | null>(null);

    // Reset to idle when parent signals an external reset (filter change, etc.)
    useEffect(() => {
        if (resetKey > 0) {
            setMode('idle');
            setActiveChip(null);
        }
    }, [resetKey]);

    const enterMode = (m: 'check' | 'consent') => {
        setMode(m);
        setActiveChip(null);
        onModeChange(m);
    };

    const handleBack = () => {
        setMode('idle');
        setActiveChip(null);
        onModeChange('idle');
        // Selection is preserved so user can switch modes without losing their picks
    };

    const exitMode = () => {
        setMode('idle');
        setActiveChip(null);
        onClearSelection();
        onModeChange('idle');
    };

    const handleChip = (value: string) => {
        setActiveChip(value);
        onQuickSelect(value);
    };

    const handleClear = () => {
        setActiveChip(null);
        onClearSelection();
    };

    const estimatedMins = Math.ceil(selectedCount * 0.5);
    const displayCount = selectedCount;

    const Chip: React.FC<{
        value: string;
        label: string;
        icon: React.ReactNode;
        modeColor: 'check' | 'consent' | 'scope';
    }> = ({ value, label, icon, modeColor }) => {
        const isActive = activeChip === value;

        const activeStyles =
            modeColor === 'check'
                ? 'bg-green-600 text-white border-green-600'
                : modeColor === 'consent'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-indigo-500 text-white border-indigo-500';

        return (
            <button
                onClick={() => handleChip(value)}
                disabled={isLoading}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all disabled:opacity-50 whitespace-nowrap ${
                    isActive
                        ? activeStyles
                        : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700 hover:text-white hover:border-gray-500'
                }`}
            >
                {isLoading && activeChip === value ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                    isActive ? null : icon
                )}
                {label}
            </button>
        );
    };

    // ── Idle state ──────────────────────────────────────────────────────────
    if (mode === 'idle') {
        if (displayCount === 0) return null;

        return (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
                <div className="bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3.5 flex items-center gap-5 border border-gray-700">
                    {/* Count */}
                    <div className="flex items-center gap-2.5 flex-shrink-0">
                        <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#69BE28' }}>
                            <Users className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold leading-tight">
                                {displayCount} driver{displayCount !== 1 ? 's' : ''} selected
                            </p>
                            <p className="text-xs text-gray-400">What would you like to do?</p>
                        </div>
                    </div>

                    <div className="h-8 w-px bg-gray-700" />

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => enterMode('check')}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all hover:brightness-110 whitespace-nowrap"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            <CheckSquare className="w-4 h-4" />
                            Run licence checks
                        </button>
                        <button
                            onClick={() => enterMode('consent')}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-500 transition-colors whitespace-nowrap"
                        >
                            <Mail className="w-4 h-4" />
                            Send consent forms
                        </button>
                    </div>

                    <div className="h-8 w-px bg-gray-700" />

                    {/* Clear */}
                    <button
                        onClick={handleClear}
                        className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap"
                    >
                        <X className="w-4 h-4" />
                        Clear
                    </button>
                </div>
            </div>
        );
    }

    // ── Shared floating panel wrapper ────────────────────────────────────────
    const isCheck = mode === 'check';
    const chips = isCheck ? CHECK_CHIPS : CONSENT_CHIPS;
    const accentColor = isCheck ? '#69BE28' : '#2563eb';
    const modeLabel = isCheck ? 'Licence check mode' : 'Consent mode';
    const ModeIcon = isCheck ? CheckSquare : Mail;

    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40" style={{ minWidth: '660px', maxWidth: '780px', width: 'max-content' }}>
            <div className="bg-gray-900 text-white rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">

                {/* Header row */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-700">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors flex-shrink-0"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                    <div className="h-4 w-px bg-gray-700" />
                    <div className="flex items-center gap-2 flex-1">
                        <ModeIcon className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
                        <span className="text-sm font-semibold">{modeLabel}</span>
                        <span className="text-xs text-gray-500">· select drivers or use smart filters below</span>
                    </div>
                    <button
                        onClick={exitMode}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors flex-shrink-0"
                    >
                        <X className="w-3.5 h-3.5" />
                        Cancel
                    </button>
                </div>

                {/* Chips rows */}
                <div className="px-5 pt-3 pb-2 bg-gray-800 border-b border-gray-700 space-y-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-gray-500 mr-1 whitespace-nowrap flex-shrink-0">Smart select:</span>
                        {chips.map(c => (
                            <Chip key={c.value} value={c.value} label={c.label} icon={c.icon} modeColor={isCheck ? 'check' : 'consent'} />
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500 mr-1 whitespace-nowrap flex-shrink-0">Scope:</span>
                        {SCOPE_CHIPS.map(c => (
                            <Chip key={c.value} value={c.value} label={c.label} icon={c.icon} modeColor="scope" />
                        ))}
                    </div>
                </div>

                {/* Footer row */}
                <div className="flex items-center justify-between gap-6 px-5 py-3">
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg" style={{ backgroundColor: accentColor }}>
                                <Users className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="text-sm font-semibold">
                                {displayCount === 0
                                    ? 'No drivers selected'
                                    : `${displayCount} driver${displayCount !== 1 ? 's' : ''} selected`}
                            </span>
                        </div>
                        {displayCount > 0 && isCheck && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Est. ~{estimatedMins} min{estimatedMins !== 1 ? 's' : ''}
                            </span>
                        )}
                        {displayCount > 0 && (
                            <button
                                onClick={handleClear}
                                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {isCheck ? (
                        <button
                            onClick={onStartBulkCheck}
                            disabled={displayCount === 0 || isStartingBulkCheck}
                            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all disabled:opacity-40 hover:brightness-110 flex-shrink-0"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            {isStartingBulkCheck ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Starting...</>
                            ) : (
                                <>Run checks{displayCount > 0 ? ` for ${displayCount} driver${displayCount !== 1 ? 's' : ''}` : ''}</>
                            )}
                        </button>
                    ) : (
                        <button
                            onClick={onStartBulkConsent}
                            disabled={displayCount === 0 || isStartingBulkConsent}
                            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg transition-all disabled:opacity-40 hover:bg-blue-500 flex-shrink-0"
                        >
                            {isStartingBulkConsent ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                            ) : (
                                <><Send className="w-4 h-4" /> Send forms{displayCount > 0 ? ` to ${displayCount} driver${displayCount !== 1 ? 's' : ''}` : ''}</>
                            )}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default SelectionToolbar;
